import type { CommonData } from '@/app/common/createCommonData.ts';
import type { ResultClickedPayload } from '@/app/common/starTrack/events/resultClicked.ts';
import { RESULT_CLICKED, RESULT_CLICKED_VERSION } from '@/app/common/starTrack/events/resultClicked.ts';
import type { MyPriceLoginPopupClickedPayload } from '@/app/common/starTrack/events/myPriceLoginPopupClicked.ts';
import {
  MYPRICE_LOGIN_POPUP_CLICKED,
  MYPRICE_LOGIN_POPUP_CLICKED_VERSION,
} from '@/app/common/starTrack/events/myPriceLoginPopupClicked.ts';
import type { MyPriceLoginBannerClickedPayload } from '@/app/common/starTrack/events/myPriceLoginBannerClicked.ts';
import {
  MYPRICE_LOGIN_BANNER_CLICKED,
  MYPRICE_LOGIN_BANNER_CLICKED_VERSION,
} from '@/app/common/starTrack/events/myPriceLoginBannerClicked.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { MapOutsideDestinationHotelsRetrievedPayload } from '@/app/common/starTrack/events/mapOutsideDestinationHotelsRetrieved.ts';
import {
  MAP_OUTSIDE_DESTINATION_HOTELS_RETRIEVED,
  MAP_OUTSIDE_DESTINATION_HOTELS_RETRIEVED_VERSION,
} from '@/app/common/starTrack/events/mapOutsideDestinationHotelsRetrieved.ts';
import type { FlightPageFilterPayload } from '@/app/common/starTrack/events/flightPageFilter.ts';
import { FLIGHT_PAGE_FILTER, FLIGHT_PAGE_FILTER_VERSION } from '@/app/common/starTrack/events/flightPageFilter.ts';
import type { LoyaltyBannerClickedPayload } from '@/app/common/starTrack/events/loyaltyBannerClicked.ts';
import {
  LOYALTY_BANNER_CLICKED,
  LOYALTY_BANNER_CLICKED_VERSION,
} from '@/app/common/starTrack/events/loyaltyBannerClicked.ts';
import type { LoyaltyBannerShownPayload } from '@/app/common/starTrack/events/loyaltyBannerShown.ts';
import {
  LOYALTY_BANNER_SHOWN,
  LOYALTY_BANNER_SHOWN_VERSION,
} from '@/app/common/starTrack/events/loyaltyBannerShown.ts';
import type { SearchDefaultFlightPayload } from '@/app/common/starTrack/events/searchDefaultFlight.ts';
import {
  SEARCH_DEFAULT_FLIGHT,
  SEARCH_DEFAULT_FLIGHT_VERSION,
} from '@/app/common/starTrack/events/searchDefaultFlight.ts';
import type { DpShownFlightsPayload } from '@/app/common/starTrack/events/dpShownFlights.ts';
import { DP_SHOWN_FLIGHTS, DP_SHOWN_FLIGHTS_VERSION } from '@/app/common/starTrack/events/dpShownFlights.ts';
import { ENVIRONMENT_NAME, getEnvironment } from '@/server/environment.ts';
import type { StarTrackEventData } from '@/app/common/starTrack/startrackTypes.ts';
import type { Environment } from '@/domainLogic/index.ts';
import type { ResultPageFilterResetPayload } from '@/app/common/starTrack/events/resultPageFilterReset.ts';
import {
  RESULT_PAGE_FILTER_RESET,
  RESULT_PAGE_FILTER_RESET_VERSION,
} from '@/app/common/starTrack/events/resultPageFilterReset.ts';
import { createStarTrackEventBatcher, type StarTrackEventBatcher } from './tracking/starTrackEventBatcher.ts';
import type { ResultShownPayload } from './events/resultShown.ts';
import { RESULT_SHOWN, RESULT_SHOWN_VERSION } from './events/resultShown.ts';
import type { ResultPageFilterPayload } from './events/resultPageFilter';
import { RESULT_PAGE_FILTER, RESULT_PAGE_FILTER_VERSION } from './events/resultPageFilter';
import type { ResultPageFilterNoResultPayload } from './events/resultPageFilterNoResult';
import { RESULT_PAGE_FILTER_NO_RESULT, RESULT_PAGE_FILTER_NO_RESULT_VERSION } from './events/resultPageFilterNoResult';
import type { ResultPageMapActionPayload } from './events/resultPageMapAction';
import { RESULT_PAGE_MAP_ACTION_VERSION, RESULT_PAGE_MAP_ACTION } from './events/resultPageMapAction';
import type { ResultPageSortResultPayload } from './events/resultPageSortResult';
import { RESULT_PAGE_SORT_RESULT, RESULT_PAGE_SORT_RESULT_VERSION } from './events/resultPageSortResult';
import type { SearchServedPayload } from './events/searchServed';
import { SEARCH_SERVED, SEARCH_SERVED_VERSION } from './events/searchServed';
import type { SearchRequestedPayload } from './events/searchRequested';
import { SEARCH_REQUESTED, SEARCH_REQUESTED_VERSION } from './events/searchRequested';
import type { SearchAdditionalParameterPayload } from './events/searchAdditionalParameter';
import { SEARCH_ADDITIONAL_PARAMETER, SEARCH_ADDITIONAL_PARAMETER_VERSION } from './events/searchAdditionalParameter';
import type { BlockingErrorShownPayload } from './events/blockingErrorShown.ts';
import { BLOCKING_ERROR_SHOWN, BLOCKING_ERROR_SHOWN_VERSION } from './events/blockingErrorShown.ts';
import type { BrandedFaresAvailablePayload } from './events/brandedFaresAvailable.ts';
import { BRANDED_FARES_AVAILABLE, BRANDED_FARES_AVAILABLE_VERSION } from './events/brandedFaresAvailable.ts';
import type { BrandedFaresClickedPayload } from './events/brandedFaresClicked.ts';
import { BRANDED_FARES_CLICKED, BRANDED_FARES_CLICKED_VERSION } from './events/brandedFaresClicked.ts';
import type { BrandedFaresShownPayload } from './events/brandedFaresShown.ts';
import { BRANDED_FARES_SHOWN, BRANDED_FARES_SHOWN_VERSION } from './events/brandedFaresShown.ts';
import type { CartChangeAmountPayload } from './events/cartChangeAmount.ts';
import { CART_CHANGE_AMOUNT, CART_CHANGE_AMOUNT_VERSION } from './events/cartChangeAmount.ts';
import type { ChangeFlightLinkClickedPayload } from './events/changeFlightLinkClicked.ts';
import { CHANGE_FLIGHT_LINK_CLICKED, CHANGE_FLIGHT_LINK_CLICKED_VERSION } from './events/changeFlightLinkClicked.ts';
import type { ChangeFlightPageShownPayload } from './events/changeFlightPageShown.ts';
import { CHANGE_FLIGHT_PAGE_SHOWN, CHANGE_FLIGHT_PAGE_SHOWN_VERSION } from './events/changeFlightPageShown.ts';
import type { ChangeRoomLinkClickedPayload } from './events/changeRoomLinkClicked.ts';
import { CHANGE_ROOM_LINK_CLICKED, CHANGE_ROOM_LINK_CLICKED_VERSION } from './events/changeRoomLinkClicked.ts';
import type { ChooseFlightShownPayload } from './events/chooseFlightShown.ts';
import { CHOOSE_FLIGHT_SHOWN, CHOOSE_FLIGHT_SHOWN_VERSION } from './events/chooseFlightShown.ts';
import type { ClickToCheckoutPayload } from './events/clickToCheckout.ts';
import { CLICK2CHECKOUT, CLICK2CHECKOUT_VERSION } from './events/clickToCheckout.ts';
import type { DetailBookClickedPayload } from './events/detailBookClicked.ts';
import { DETAIL_BOOK_CLICKED, DETAIL_BOOK_CLICKED_VERSION } from './events/detailBookClicked.ts';
import type { DetailPageImageGalleryClickedPayload } from './events/detailPageImageGalleryClicked.ts';
import {
  DETAIL_PAGE_IMAGE_GALLERY_CLICKED,
  DETAIL_PAGE_IMAGE_GALLERY_CLICKED_VERSION,
} from './events/detailPageImageGalleryClicked.ts';
import type { DetailPageSeeOtherStaysOptionsClickedPayload } from './events/detailPageSeeOtherStaysOptionsClicked.ts';
import {
  DETAIL_PAGE_SEE_OTHER_STAYS_OPTIONS_CLICKED,
  DETAIL_PAGE_SEE_OTHER_STAYS_OPTIONS_CLICKED_VERSION,
} from './events/detailPageSeeOtherStaysOptionsClicked.ts';
import type { DetailPageUserActionPayload } from './events/detailPageUserAction.ts';
import { DETAIL_PAGE_USER_ACTION, DETAIL_PAGE_USER_ACTION_VERSION } from './events/detailPageUserAction.ts';
import type { FlightChangedPayload } from './events/flightChanged.ts';
import { FLIGHT_CHANGED, FLIGHT_CHANGED_VERSION } from './events/flightChanged.ts';
import type { FlightClickedPayload } from './events/flightClicked.ts';
import { FLIGHT_CLICKED, FLIGHT_CLICKED_VERSION } from './events/flightClicked.ts';
import type { FlightDetailsClickedPayload } from './events/flightDetailsClicked.ts';
import { FLIGHT_DETAILS_CLICKED, FLIGHT_DETAILS_CLICKED_VERSION } from './events/flightDetailsClicked.ts';
import type { FlightDetailsOpenedPayload } from './events/flightDetailsOpened.ts';
import { FLIGHT_DETAILS_OPENED, FLIGHT_DETAILS_OPENED_VERSION } from './events/flightDetailsOpened.ts';
import type { HotelDetailShownPayload } from './events/hotelDetailShown.ts';
import { HOTEL_DETAIL_SHOWN, HOTEL_DETAIL_SHOWN_VERSION } from './events/hotelDetailShown.ts';
import type { HotelRoomClickedPayload } from './events/hotelRoomClicked.ts';
import { HOTEL_ROOM_CLICKED, HOTEL_ROOM_CLICKED_VERSION } from './events/hotelRoomClicked.ts';
import type { HotelRoomOptionClickedPayload } from './events/hotelRoomOptionClicked.ts';
import { HOTEL_ROOM_OPTION_CLICKED_VERSION, HOTEL_ROOM_OPTION_CLICKED } from './events/hotelRoomOptionClicked.ts';
import type { PopupCancellationPolicyClickedPayload } from './events/popupCancellationPolicyClicked.ts';
import {
  POPUP_CANCELLATION_POLICY_CLICKED,
  POPUP_CANCELLATION_POLICY_CLICKED_VERSION,
} from './events/popupCancellationPolicyClicked.ts';
import type { PopupCancellationPolicyShownPayload } from './events/popupCancellationPolicyShown.ts';
import {
  POPUP_CANCELLATION_POLICY_SHOWN,
  POPUP_CANCELLATION_POLICY_SHOWN_VERSION,
} from './events/popupCancellationPolicyShown.ts';
import type { PriceCalendarClickedPayload } from './events/priceCalendarClicked.ts';
import {
  PRODUCT_DETAIL_PRICE_CALENDAR_CLICKED,
  PRODUCT_DETAIL_PRICE_CALENDAR_CLICKED_VERSION,
} from './events/priceCalendarClicked.ts';
import type { PriceCalendarInteractionPayload } from './events/priceCalendarInteraction.ts';
import { PRICE_CALENDAR_INTERACTION, PRICE_CALENDAR_INTERACTION_VERSION } from './events/priceCalendarInteraction.ts';
import type { PriceCalendarShownPayload } from './events/priceCalendarShown.ts';
import {
  PRODUCT_DETAIL_PRICE_CALENDAR_SHOWN,
  PRODUCT_DETAIL_PRICE_CALENDAR_SHOWN_VERSION,
} from './events/priceCalendarShown.ts';
import type { PriceToggleSelectedPayload } from './events/priceToggleSelected.ts';
import { PRICE_TOGGLE_SELECTED, PRICE_TOGGLE_SELECTED_VERSION } from './events/priceToggleSelected.ts';
import type { ResultPageShowMapClickedPayload } from './events/resultPageShowMapClicked.ts';
import {
  RESULT_PAGE_SHOW_MAP_CLICKED,
  RESULT_PAGE_SHOW_MAP_CLICKED_VERSION,
} from './events/resultPageShowMapClicked.ts';
import type { RoomsDetailInfoPayload } from './events/roomsDetailInfo.ts';
import { ROOMS_DETAIL_INFO, ROOMS_DETAIL_INFO_VERSION } from './events/roomsDetailInfo.ts';
import type { SummaryDetailsShownPayload } from './events/summaryDetailsShown.ts';
import { SUMMARY_DETAILS_SHOWN, SUMMARY_DETAILS_SHOWN_VERSION } from './events/summaryDetailsShown.ts';
import type { VoucherInfoClickedPayload } from './events/voucherInfoClicked.ts';
import { VOUCHER_INFO_CLICKED, VOUCHER_INFO_CLICKED_VERSION } from './events/voucherInfoClicked.ts';
import type { VoucherInfoCodeCopiedPayload } from './events/voucherInfoCodeCopied.ts';
import { VOUCHER_INFO_CODE_COPIED, VOUCHER_INFO_CODE_COPIED_VERSION } from './events/voucherInfoCodeCopied.ts';

export type StarTrackParams = {
  localization: Localization;
  searchInfoData: SearchInfoData;
  commonData: CommonData;
  /** How often queued events are sent to the BE, in ms. Defaults to 10s. */
  flushIntervalMs?: number;
};

export type StarTrack = {
  blockingErrorShown: (payload: BlockingErrorShownPayload) => Promise<void | undefined>;
  brandedFaresAvailable: (payload: BrandedFaresAvailablePayload) => Promise<void | undefined>;
  brandedFaresClicked: (payload: BrandedFaresClickedPayload) => Promise<void | undefined>;
  brandedFaresShown: (payload: BrandedFaresShownPayload) => Promise<void | undefined>;
  cartChangeAmount: (payload: CartChangeAmountPayload) => Promise<void | undefined>;
  changeFlightLinkClicked: (payload: ChangeFlightLinkClickedPayload) => Promise<void | undefined>;
  changeFlightPageShown: (payload: ChangeFlightPageShownPayload) => Promise<void | undefined>;
  chooseFlightShown: (payload: ChooseFlightShownPayload) => Promise<void | undefined>;
  changeRoomLinkClicked: (payload: ChangeRoomLinkClickedPayload) => Promise<void | undefined>;
  clickToCheckout: (payload: ClickToCheckoutPayload) => Promise<void | undefined>;
  detailBookClicked: (payload: DetailBookClickedPayload) => Promise<void | undefined>;
  detailPageImageGalleryClicked: (payload: DetailPageImageGalleryClickedPayload) => Promise<void | undefined>;
  detailPageSeeOtherStaysOptionsClicked: (
    payload: DetailPageSeeOtherStaysOptionsClickedPayload
  ) => Promise<void | undefined>;
  detailPageUserAction: (payload: DetailPageUserActionPayload) => Promise<void | undefined>;
  dpShownFlights: (payload: DpShownFlightsPayload) => Promise<void | undefined>;
  flightChanged: (payload: FlightChangedPayload) => Promise<void | undefined>;
  flightClicked: (payload: FlightClickedPayload) => Promise<void | undefined>;
  flightDetailsClicked: (payload: FlightDetailsClickedPayload) => Promise<void | undefined>;
  flightDetailsOpened: (payload: FlightDetailsOpenedPayload) => Promise<void | undefined>;
  flightPageFilter: (payload: FlightPageFilterPayload) => Promise<void | undefined>;
  hotelDetailShown: (payload: HotelDetailShownPayload) => Promise<void | undefined>;
  hotelRoomClick: (payload: HotelRoomClickedPayload) => Promise<void | undefined>;
  hotelRoomOptionClicked: (payload: HotelRoomOptionClickedPayload) => Promise<void | undefined>;
  loyaltyBannerClicked: (payload: LoyaltyBannerClickedPayload) => Promise<void | undefined>;
  loyaltyBannerShown: (payload: LoyaltyBannerShownPayload) => Promise<void | undefined>;
  mapOutsideDestinationHotelsRetrieved: (
    payload: MapOutsideDestinationHotelsRetrievedPayload
  ) => Promise<void | undefined>;
  myPriceLoginBannerClicked: (payload: MyPriceLoginBannerClickedPayload) => Promise<void | undefined>;
  myPriceLoginPopupClicked: (payload: MyPriceLoginPopupClickedPayload) => Promise<void | undefined>;
  popupCancellationPolicyClicked: (payload: PopupCancellationPolicyClickedPayload) => Promise<void | undefined>;
  popupCancellationPolicyShown: (payload: PopupCancellationPolicyShownPayload) => Promise<void | undefined>;
  priceCalendarClicked: (payload: PriceCalendarClickedPayload) => Promise<void | undefined>;
  priceCalendarInteraction: (payload: PriceCalendarInteractionPayload) => Promise<void | undefined>;
  priceCalendarShown: (payload: PriceCalendarShownPayload) => Promise<void | undefined>;
  priceToggleSelected: (payload: PriceToggleSelectedPayload) => Promise<void | undefined>;
  resultClicked: (payload: ResultClickedPayload) => Promise<void | undefined>;
  resultPageFilter: (payload: ResultPageFilterPayload) => Promise<void | undefined>;
  resultPageFilterNoResult: (payload: ResultPageFilterNoResultPayload) => Promise<void | undefined>;
  resultPageFilterReset: (payload: ResultPageFilterResetPayload) => Promise<void | undefined>;
  resultPageMapAction: (payload: ResultPageMapActionPayload) => Promise<void | undefined>;
  resultShown: (payload: ResultShownPayload) => Promise<void | undefined>;
  resultPageShowMapClicked: (payload: ResultPageShowMapClickedPayload) => Promise<void | undefined>;
  resultPageSortResult: (payload: ResultPageSortResultPayload) => Promise<void | undefined>;
  roomsDetailInfo: (payload: RoomsDetailInfoPayload) => Promise<void | undefined>;
  roomsDetailInfoMultiple: (payload: RoomsDetailInfoPayload[]) => Promise<void | undefined>;
  searchAdditionalParameter: (payload: SearchAdditionalParameterPayload) => Promise<void | undefined>;
  searchDefaultFlight: (payload: SearchDefaultFlightPayload) => Promise<void | undefined>;
  searchRequested: (payload: SearchRequestedPayload) => Promise<void | undefined>;
  searchServed: (payload: SearchServedPayload) => Promise<void | undefined>;
  summaryDetailsShown: (payload: SummaryDetailsShownPayload) => Promise<void | undefined>;
  voucherInfoClicked: (payload: VoucherInfoClickedPayload) => Promise<void | undefined>;
  voucherInfoCodeCopied: (payload: VoucherInfoCodeCopiedPayload) => Promise<void | undefined>;
};

// A single batcher is shared across StarTrack instances: `createStarTrack` is
// re-invoked on every new search (the traceParent changes), so a per-instance
// timer / listeners would leak. We keep one queue + timer and just update the
// environment / traceParent used for subsequent flushes.
let eventBatcher: StarTrackEventBatcher | null = null;

const getEventBatcher = (
  environment: Environment,
  traceParent: string,
  commonData: CommonData,
  flushIntervalMs?: number
): StarTrackEventBatcher => {
  if (!eventBatcher) {
    eventBatcher = createStarTrackEventBatcher({ environment, traceParent, commonData, flushIntervalMs });
  } else {
    eventBatcher.reconfigure({ environment, traceParent, commonData });
  }
  return eventBatcher;
};

export const createStarTrack = ({
  localization,
  searchInfoData,
  commonData,
  flushIntervalMs,
}: StarTrackParams): StarTrack => {
  const { environments } = localization;
  const { searchTraceParent } = searchInfoData;
  const environment = getEnvironment(ENVIRONMENT_NAME.APP, environments);

  const createStarTrackEventData = <T>(name: string, version: string, payloads: T[]): StarTrackEventData<T>[] =>
    payloads.map((payload) => ({
      name,
      version,
      payload,
      traceparent: searchTraceParent,
      dateTime: new Date().toISOString(),
    }));

  // Events are queued and sent in a single batched call to `/api/events`
  // every `flushIntervalMs` (default 10s) — see `createStarTrackEventBatcher`.
  const sendStarTrackEvents = <T>(name: string, version: string, payload: T[]): Promise<undefined> => {
    try {
      getEventBatcher(environment, searchTraceParent, commonData, flushIntervalMs).enqueue(
        createStarTrackEventData(name, version, payload)
      );
    } catch (e) {
      console.log(e);
    }
    return Promise.resolve(undefined);
  };

  return {
    // DISABLED EVENTS IN SEARCH PAGE [https://jira.lastminute.com/browse/SLIDERS-810]
    // changeTransportLinkClicked(params: ChangeTransportLinkClickedParams): void {},
    // changeTransportPageShown(params: ChangeTransportPageShownParams): void {},
    // hotelMapDotClicked(params: HotelMapDotClickedPayload): void {},
    // resultPageMapListViewChanged(params: ResultPageMapListViewChangedPayloadParams): void {},
    // transportChanged(params: TransportChangedParams): void {},
    loyaltyBannerClicked: (payload: LoyaltyBannerClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(LOYALTY_BANNER_CLICKED, LOYALTY_BANNER_CLICKED_VERSION, [payload]),
    loyaltyBannerShown: (payload: LoyaltyBannerShownPayload): Promise<void | undefined> =>
      sendStarTrackEvents(LOYALTY_BANNER_SHOWN, LOYALTY_BANNER_SHOWN_VERSION, [payload]),
    resultClicked: (payload: ResultClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(RESULT_CLICKED, RESULT_CLICKED_VERSION, [payload]),
    resultShown: (payload: ResultShownPayload): Promise<void | undefined> =>
      sendStarTrackEvents(RESULT_SHOWN, RESULT_SHOWN_VERSION, [payload]),
    searchRequested: (payload: SearchRequestedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(SEARCH_REQUESTED, SEARCH_REQUESTED_VERSION, [payload]),
    myPriceLoginPopupClicked: (payload: MyPriceLoginPopupClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(MYPRICE_LOGIN_POPUP_CLICKED, MYPRICE_LOGIN_POPUP_CLICKED_VERSION, [payload]),
    myPriceLoginBannerClicked: (payload: MyPriceLoginBannerClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(MYPRICE_LOGIN_BANNER_CLICKED, MYPRICE_LOGIN_BANNER_CLICKED_VERSION, [payload]),
    resultPageFilter: (payload: ResultPageFilterPayload): Promise<void | undefined> =>
      sendStarTrackEvents(RESULT_PAGE_FILTER, RESULT_PAGE_FILTER_VERSION, [payload]),
    flightPageFilter: (payload: FlightPageFilterPayload): Promise<void | undefined> =>
      sendStarTrackEvents(FLIGHT_PAGE_FILTER, FLIGHT_PAGE_FILTER_VERSION, [payload]),
    resultPageFilterNoResult: (payload: ResultPageFilterNoResultPayload): Promise<void | undefined> =>
      sendStarTrackEvents(RESULT_PAGE_FILTER_NO_RESULT, RESULT_PAGE_FILTER_NO_RESULT_VERSION, [payload]),
    resultPageFilterReset: (payload: ResultPageFilterResetPayload): Promise<void | undefined> =>
      sendStarTrackEvents(RESULT_PAGE_FILTER_RESET, RESULT_PAGE_FILTER_RESET_VERSION, [payload]),
    resultPageMapAction: (payload: ResultPageMapActionPayload): Promise<void | undefined> =>
      sendStarTrackEvents(RESULT_PAGE_MAP_ACTION, RESULT_PAGE_MAP_ACTION_VERSION, [payload]),
    searchServed: (payload: SearchServedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(SEARCH_SERVED, SEARCH_SERVED_VERSION, [payload]),
    resultPageShowMapClicked: (payload: ResultPageShowMapClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(RESULT_PAGE_SHOW_MAP_CLICKED, RESULT_PAGE_SHOW_MAP_CLICKED_VERSION, [payload]),
    resultPageSortResult: (payload: ResultPageSortResultPayload): Promise<void | undefined> =>
      sendStarTrackEvents(RESULT_PAGE_SORT_RESULT, RESULT_PAGE_SORT_RESULT_VERSION, [payload]),
    searchAdditionalParameter: (payload: SearchAdditionalParameterPayload): Promise<void | undefined> =>
      sendStarTrackEvents(SEARCH_ADDITIONAL_PARAMETER, SEARCH_ADDITIONAL_PARAMETER_VERSION, [payload]),
    searchDefaultFlight: (payload: SearchDefaultFlightPayload): Promise<void | undefined> =>
      sendStarTrackEvents(SEARCH_DEFAULT_FLIGHT, SEARCH_DEFAULT_FLIGHT_VERSION, [payload]),
    hotelDetailShown: (payload: HotelDetailShownPayload): Promise<void | undefined> =>
      sendStarTrackEvents(HOTEL_DETAIL_SHOWN, HOTEL_DETAIL_SHOWN_VERSION, [payload]),
    hotelRoomClick: (payload: HotelRoomClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(HOTEL_ROOM_CLICKED, HOTEL_ROOM_CLICKED_VERSION, [payload]),
    hotelRoomOptionClicked: (payload: HotelRoomOptionClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(HOTEL_ROOM_OPTION_CLICKED, HOTEL_ROOM_OPTION_CLICKED_VERSION, [payload]),
    popupCancellationPolicyShown: (payload: PopupCancellationPolicyShownPayload): Promise<void | undefined> =>
      sendStarTrackEvents(POPUP_CANCELLATION_POLICY_SHOWN, POPUP_CANCELLATION_POLICY_SHOWN_VERSION, [payload]),
    popupCancellationPolicyClicked: (payload: PopupCancellationPolicyClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(POPUP_CANCELLATION_POLICY_CLICKED, POPUP_CANCELLATION_POLICY_CLICKED_VERSION, [payload]),
    summaryDetailsShown: (payload: SummaryDetailsShownPayload): Promise<void | undefined> =>
      sendStarTrackEvents(SUMMARY_DETAILS_SHOWN, SUMMARY_DETAILS_SHOWN_VERSION, [payload]),
    chooseFlightShown: (payload: ChooseFlightShownPayload): Promise<void | undefined> =>
      sendStarTrackEvents(CHOOSE_FLIGHT_SHOWN, CHOOSE_FLIGHT_SHOWN_VERSION, [payload]),
    flightClicked: (payload: FlightClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(FLIGHT_CLICKED, FLIGHT_CLICKED_VERSION, [payload]),
    flightDetailsClicked: (payload: FlightDetailsClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(FLIGHT_DETAILS_CLICKED, FLIGHT_DETAILS_CLICKED_VERSION, [payload]),
    roomsDetailInfo: (payload: RoomsDetailInfoPayload): Promise<void | undefined> =>
      sendStarTrackEvents(ROOMS_DETAIL_INFO, ROOMS_DETAIL_INFO_VERSION, [payload]),
    roomsDetailInfoMultiple: (payloads: RoomsDetailInfoPayload[]): Promise<void | undefined> =>
      sendStarTrackEvents(ROOMS_DETAIL_INFO, ROOMS_DETAIL_INFO_VERSION, payloads),
    cartChangeAmount: (payload: CartChangeAmountPayload): Promise<void | undefined> =>
      sendStarTrackEvents(CART_CHANGE_AMOUNT, CART_CHANGE_AMOUNT_VERSION, [payload]),
    blockingErrorShown: (payload: BlockingErrorShownPayload): Promise<void | undefined> =>
      sendStarTrackEvents(BLOCKING_ERROR_SHOWN, BLOCKING_ERROR_SHOWN_VERSION, [payload]),
    priceCalendarClicked: (payload: PriceCalendarClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(PRODUCT_DETAIL_PRICE_CALENDAR_CLICKED, PRODUCT_DETAIL_PRICE_CALENDAR_CLICKED_VERSION, [
        payload,
      ]),
    priceCalendarShown: (payload: PriceCalendarShownPayload): Promise<void | undefined> =>
      sendStarTrackEvents(PRODUCT_DETAIL_PRICE_CALENDAR_SHOWN, PRODUCT_DETAIL_PRICE_CALENDAR_SHOWN_VERSION, [payload]),
    priceCalendarInteraction: (payload: PriceCalendarInteractionPayload): Promise<void | undefined> =>
      sendStarTrackEvents(PRICE_CALENDAR_INTERACTION, PRICE_CALENDAR_INTERACTION_VERSION, [payload]),
    priceToggleSelected: (payload: PriceToggleSelectedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(PRICE_TOGGLE_SELECTED, PRICE_TOGGLE_SELECTED_VERSION, [payload]),
    brandedFaresAvailable: (payload: BrandedFaresAvailablePayload): Promise<void | undefined> =>
      sendStarTrackEvents(BRANDED_FARES_AVAILABLE, BRANDED_FARES_AVAILABLE_VERSION, [payload]),
    brandedFaresShown: (payload: BrandedFaresShownPayload): Promise<void | undefined> =>
      sendStarTrackEvents(BRANDED_FARES_SHOWN, BRANDED_FARES_SHOWN_VERSION, [payload]),
    brandedFaresClicked: (payload: BrandedFaresClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(BRANDED_FARES_CLICKED, BRANDED_FARES_CLICKED_VERSION, [payload]),
    clickToCheckout: (payload: ClickToCheckoutPayload): Promise<void | undefined> => {
      // CLICK2CHECKOUT is a conversion event that must not be lost on navigation.
      // sendImmediate bypasses the queue and returns a real promise: the caller
      // awaits it before calling redirectToCheckout() / window.stop(), so the
      // send is guaranteed to be settled (or timed out) before the page unloads.
      try {
        return getEventBatcher(environment, searchTraceParent, commonData, flushIntervalMs).sendImmediate(
          createStarTrackEventData(CLICK2CHECKOUT, CLICK2CHECKOUT_VERSION, [payload])
        );
      } catch (e) {
        console.log(e);
        return Promise.resolve(undefined);
      }
    },
    changeFlightLinkClicked: (payload: ChangeFlightLinkClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(CHANGE_FLIGHT_LINK_CLICKED, CHANGE_FLIGHT_LINK_CLICKED_VERSION, [payload]),
    changeFlightPageShown: (payload: ChangeFlightPageShownPayload): Promise<void | undefined> =>
      sendStarTrackEvents(CHANGE_FLIGHT_PAGE_SHOWN, CHANGE_FLIGHT_PAGE_SHOWN_VERSION, [payload]),
    changeRoomLinkClicked: (payload: ChangeRoomLinkClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(CHANGE_ROOM_LINK_CLICKED, CHANGE_ROOM_LINK_CLICKED_VERSION, [payload]),
    detailPageImageGalleryClicked: (payload: DetailPageImageGalleryClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(DETAIL_PAGE_IMAGE_GALLERY_CLICKED, DETAIL_PAGE_IMAGE_GALLERY_CLICKED_VERSION, [payload]),
    detailPageSeeOtherStaysOptionsClicked: (
      payload: DetailPageSeeOtherStaysOptionsClickedPayload
    ): Promise<void | undefined> =>
      sendStarTrackEvents(
        DETAIL_PAGE_SEE_OTHER_STAYS_OPTIONS_CLICKED,
        DETAIL_PAGE_SEE_OTHER_STAYS_OPTIONS_CLICKED_VERSION,
        [payload]
      ),
    detailPageUserAction: (payload: DetailPageUserActionPayload): Promise<void | undefined> =>
      sendStarTrackEvents(DETAIL_PAGE_USER_ACTION, DETAIL_PAGE_USER_ACTION_VERSION, [payload]),
    flightChanged: (payload: FlightChangedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(FLIGHT_CHANGED, FLIGHT_CHANGED_VERSION, [payload]),
    flightDetailsOpened: (payload: FlightDetailsOpenedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(FLIGHT_DETAILS_OPENED, FLIGHT_DETAILS_OPENED_VERSION, [payload]),
    detailBookClicked: (payload: DetailBookClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(DETAIL_BOOK_CLICKED, DETAIL_BOOK_CLICKED_VERSION, [payload]),
    voucherInfoClicked: (payload: VoucherInfoClickedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(VOUCHER_INFO_CLICKED, VOUCHER_INFO_CLICKED_VERSION, [payload]),
    voucherInfoCodeCopied: (payload: VoucherInfoCodeCopiedPayload): Promise<void | undefined> =>
      sendStarTrackEvents(VOUCHER_INFO_CODE_COPIED, VOUCHER_INFO_CODE_COPIED_VERSION, [payload]),
    mapOutsideDestinationHotelsRetrieved: (
      payload: MapOutsideDestinationHotelsRetrievedPayload
    ): Promise<void | undefined> =>
      sendStarTrackEvents(MAP_OUTSIDE_DESTINATION_HOTELS_RETRIEVED, MAP_OUTSIDE_DESTINATION_HOTELS_RETRIEVED_VERSION, [
        payload,
      ]),
    dpShownFlights: (payload: DpShownFlightsPayload): Promise<void | undefined> =>
      sendStarTrackEvents(DP_SHOWN_FLIGHTS, DP_SHOWN_FLIGHTS_VERSION, [payload]),
  };
};
